import React, { useEffect, useMemo, useState } from 'react';
import usePagination from '../utils/usePagination';

const ROWS_PER_PAGE = 10;
const SEVERITY_ORDER = { CRITICAL: 0, DEGRADED: 1 };

function compareValues(left, right, key) {
    if (key === 'severity') {
        return (SEVERITY_ORDER[left] ?? 99) - (SEVERITY_ORDER[right] ?? 99);
    }
    if (key === 'score') return Number(left || 0) - Number(right || 0);
    return String(left || '').localeCompare(String(right || ''));
}

function SortableHeader({ column, label, tooltip, activeColumn, direction, onSort }) {
    const isActive = activeColumn === column;
    return (
        <th scope="col" aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
                type="button"
                className="fh-table-sort-button"
                onClick={() => onSort(column)}
                title={tooltip}
                aria-label={tooltip ? `${label}. ${tooltip}` : label}
            >
                <span>{label}</span>
                <span className="fh-table-sort-indicator" aria-hidden="true">
                    {isActive ? (direction === 'asc' ? '↑' : '↓') : '↕'}
                </span>
            </button>
        </th>
    );
}

function MultiSelectFilter({ label, options, selectedValues, onChange, allLabel }) {
    const selected = new Set(selectedValues);
    const summary = selected.size === 0
        ? allLabel
        : selected.size === 1
            ? options.find((option) => option.value === selectedValues[0])?.label
            : `${selected.size} selected`;

    const toggleValue = (value) => {
        const next = new Set(selected);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        onChange([...next]);
    };

    return (
        <div className="fh-problematic-multiselect-label">
            <span className="fh-problematic-multiselect-title">{label}</span>
            <details className="fh-problematic-multiselect">
                <summary>{summary}</summary>
                <div className="fh-problematic-multiselect-menu">
                    {options.map((option) => (
                        <label key={option.value} className="fh-problematic-multiselect-option">
                            <input
                                type="checkbox"
                                checked={selected.has(option.value)}
                                onChange={() => toggleValue(option.value)}
                            />
                            <span>{option.label}</span>
                        </label>
                    ))}
                </div>
            </details>
        </div>
    );
}

function ProblematicDevicesTable({ devices, onDeviceSelect }) {
    const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [deviceTagFilters, setDeviceTagFilters] = useState([]);
    const [severityFilters, setSeverityFilters] = useState([]);

    const rows = useMemo(() => (Array.isArray(devices) ? devices : [])
        .filter((device) => ['DEGRADED', 'CRITICAL'].includes((device.severity || '').toUpperCase())), [devices]);

    const deviceTags = useMemo(() => [...new Set(rows
        .map((device) => device.deviceTag)
        .filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);

    const filteredRows = useMemo(() => rows.filter((device) => (
        (deviceTagFilters.length === 0 || deviceTagFilters.includes(device.deviceTag))
        && (severityFilters.length === 0 || severityFilters.includes(device.severity))
    )), [rows, deviceTagFilters, severityFilters]);

    const sortedRows = useMemo(() => [...filteredRows].sort((left, right) => {
        const comparison = compareValues(left[sortConfig.key], right[sortConfig.key], sortConfig.key);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
    }), [filteredRows, sortConfig]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredRows, sortConfig]);

    const { pageItems, totalPages, renderPageNumbers } = usePagination(
        sortedRows,
        currentPage,
        setCurrentPage,
        ROWS_PER_PAGE
    );

    const handleSort = (key) => {
        setSortConfig((previous) => ({
            key,
            direction: previous.key === key && previous.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const firstRow = sortedRows.length === 0 ? 0 : (currentPage - 1) * ROWS_PER_PAGE + 1;
    const lastRow = Math.min(currentPage * ROWS_PER_PAGE, sortedRows.length);

    return (
        <div className="fh-problematic-table-wrap">
            <div className="fh-problematic-table-filters">
                <MultiSelectFilter
                    label="Device group"
                    options={deviceTags.map((tag) => ({ value: tag, label: tag }))}
                    selectedValues={deviceTagFilters}
                    onChange={setDeviceTagFilters}
                    allLabel="All device groups"
                />
                <MultiSelectFilter
                    label="Severity Level"
                    options={[{ value: 'DEGRADED', label: 'Degraded' }, { value: 'CRITICAL', label: 'Critical' }]}
                    selectedValues={severityFilters}
                    onChange={setSeverityFilters}
                    allLabel="All severities"
                />
            </div>
            <div className="fh-problematic-table-scroll">
                <table className="fh-problematic-table">
                    <thead>
                        <tr>
                            <SortableHeader column="systemId" label="System ID" direction={sortConfig.direction} activeColumn={sortConfig.key} onSort={handleSort} />
                            <SortableHeader column="deviceTag" label="Device Group" direction={sortConfig.direction} activeColumn={sortConfig.key} onSort={handleSort} />
                            <SortableHeader
                                column="score"
                                label="Latest Device Health Score"
                                tooltip="Latest raw health score for this active device within the selected lookback period; it is not a time-averaged score."
                                direction={sortConfig.direction}
                                activeColumn={sortConfig.key}
                                onSort={handleSort}
                            />
                            <SortableHeader column="severity" label="Severity Level" direction={sortConfig.direction} activeColumn={sortConfig.key} onSort={handleSort} />
                        </tr>
                    </thead>
                    <tbody>
                        {pageItems.map((device) => (
                            <tr key={device.systemId}>
                                <td className="fh-problematic-device-id">
                                    <button type="button" onClick={() => onDeviceSelect?.(device)}>{device.systemId}</button>
                                </td>
                                <td>{device.deviceTag || 'Unassigned'}</td>
                                <td className="fh-problematic-device-score">{Math.round(device.score * 100)}/100</td>
                                <td>
                                    <span className={`fh-severity fh-severity-${(device.severity || '').toLowerCase()}`}>
                                        {device.severity || 'UNKNOWN'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {sortedRows.length === 0 && <div className="fh-problematic-table-empty">No devices match these filters.</div>}
            <div className="fh-problematic-table-footer">
                <span>Showing {firstRow}–{lastRow} of {sortedRows.length}</span>
                {totalPages > 1 && <div className="fh-problematic-pagination pagination-controls">{renderPageNumbers()}</div>}
            </div>
        </div>
    );
}

export default ProblematicDevicesTable;
